import React, { useEffect, useMemo, useRef, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";

export type FilterOption = { id: string; label: string; count?: number };

/** Toolbar filter: a button that opens a searchable tick list. Picks several values. */
export default function FilterMultiSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: FilterOption[];
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => new Set(value), [value]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      window.clearTimeout(t);
    };
  }, [open]);

  const toggle = (id: string) => {
    onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
          value.length
            ? "bg-primary/5 border-primary/40 text-primary dark:bg-primary/20 dark:text-blue-200"
            : "bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300"
        }`}
      >
        {label}
        {value.length > 0 && (
          <span className="min-w-[16px] px-1 rounded-full bg-primary text-white text-[10px] leading-4 text-center">
            {value.length}
          </span>
        )}
        <KeyboardArrowDownIcon
          sx={{ fontSize: 16 }}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 z-30 mt-1 w-60 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg">
          <div className="relative p-2 border-b border-slate-100 dark:border-slate-700">
            <SearchIcon
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              sx={{ fontSize: 14 }}
            />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="w-full pl-7 pr-2 py-1.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-md outline-none text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400"
            />
          </div>
          <ul role="listbox" aria-multiselectable className="max-h-60 overflow-y-auto py-1">
            {visible.length === 0 ? (
              <li className="px-3 py-2 text-xs text-slate-400">No matches</li>
            ) : (
              visible.map((o) => {
                const checked = selected.has(o.id);
                return (
                  <li key={o.id} role="option" aria-selected={checked}>
                    <button
                      type="button"
                      onClick={() => toggle(o.id)}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-slate-50 dark:hover:bg-slate-700 ${
                        o.count === 0 && !checked ? "opacity-50" : ""
                      }`}
                    >
                      <span
                        className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
                          checked ? "bg-primary border-primary text-white" : "border-slate-300 dark:border-slate-500"
                        }`}
                      >
                        {checked && <CheckIcon sx={{ fontSize: 11 }} />}
                      </span>
                      <span className="flex-1 truncate text-slate-700 dark:text-slate-200">{o.label}</span>
                      {o.count !== undefined && (
                        <span className="text-[10px] text-slate-400">{o.count}</span>
                      )}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
          {value.length > 0 && (
            <div className="border-t border-slate-100 dark:border-slate-700 px-3 py-1.5">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-[11px] font-medium text-primary dark:text-blue-300 hover:underline"
              >
                Clear {label.toLowerCase()}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
